import type { GeneratedChecklistRow } from './checklistRows';
import type { ManualOverrideDecision, ManualOverrideState } from './types';

type ApprovalSource = 'auto' | 'manual' | 'manual_required' | 'none';

export function buildManualOverrideState(
  rows: Array<Pick<GeneratedChecklistRow, 'id' | 'manualOverride'>>,
): ManualOverrideState {
  return Object.fromEntries(rows.flatMap((row) => (row.manualOverride ? [[row.id, row.manualOverride] as const] : [])));
}

export function getApprovalSource(
  row: Pick<GeneratedChecklistRow, 'id' | 'status'>,
  manualOverrides: ManualOverrideState,
): ApprovalSource {
  const manualOverrideStatus = getManualOverrideStatus(row, manualOverrides);

  if (manualOverrideStatus === 'approved') {
    return 'manual';
  }

  if (manualOverrideStatus === 'requires_approval') {
    return 'manual_required';
  }

  return row.status === 'approved' ? 'auto' : 'none';
}

export function getManualOverrideStatus(
  row: Pick<GeneratedChecklistRow, 'id'>,
  manualOverrides: ManualOverrideState,
): ManualOverrideDecision | undefined {
  return manualOverrides[row.id];
}

export function isRowApproved(row: Pick<GeneratedChecklistRow, 'id' | 'status'>, manualOverrides: ManualOverrideState) {
  const approvalSource = getApprovalSource(row, manualOverrides);

  return approvalSource === 'auto' || approvalSource === 'manual';
}

export function isRowManuallyApproved(row: Pick<GeneratedChecklistRow, 'id'>, manualOverrides: ManualOverrideState) {
  return getManualOverrideStatus(row, manualOverrides) === 'approved';
}

export function isRowManuallyRequired(row: Pick<GeneratedChecklistRow, 'id'>, manualOverrides: ManualOverrideState) {
  return getManualOverrideStatus(row, manualOverrides) === 'requires_approval';
}

export function getEffectiveChecklistStatus(
  row: Pick<GeneratedChecklistRow, 'id' | 'status'>,
  manualOverrides: ManualOverrideState,
) {
  if (isRowApproved(row, manualOverrides)) {
    return 'approved';
  }

  if (isRowManuallyRequired(row, manualOverrides)) {
    return 'requires approval';
  }

  return row.status;
}
